import React, { useState, useEffect } from 'react'
import { AiOutlineEye, AiOutlineEyeInvisible } from 'react-icons/ai'
import { useNavigate, useSearchParams } from 'react-router-dom'
import { useDarkMode } from '../hooks/useDarkMode'

export default function UpdatePassword() {
  const { isDarkMode } = useDarkMode()
  const [newPassword, setNewPassword] = useState('')
  const [reEnterPassword, setReEnterPassword] = useState('')
  const [showNewPassword, setShowNewPassword] = useState(false)
  const [showReEnterPassword, setShowReEnterPassword] = useState(false)
  const [error, setError] = useState('')
  const [success, setSuccess] = useState(false)
  const [loading, setLoading] = useState(false)
  const navigate = useNavigate()
  const [searchParams] = useSearchParams()
  const email = searchParams.get('email')

  useEffect(() => {
    if (!email) {
      setError('Invalid session. Please start from forgot password.')
      setTimeout(() => navigate('/forgetpassword'), 2000)
    }
  }, [email, navigate])

  const validatePassword = (pwd) => {
    // At least 8 characters, 1 uppercase, 1 lowercase, 1 number, 1 special character
    const regex = /^(?=.*[a-z])(?=.*[A-Z])(?=.*\d)(?=.*[@$!%*?&])[A-Za-z\d@$!%*?&]{8,}$/
    return regex.test(pwd)
  }

  const handleResetPassword = async () => {
    setError('')

    if (!newPassword || !reEnterPassword) {
      setError('Both fields are required')
      return
    }

    if (!validatePassword(newPassword)) {
      setError('Password must be at least 8 characters with 1 uppercase, 1 lowercase, 1 number, and 1 special character')
      return
    }

    if (newPassword !== reEnterPassword) {
      setError('Passwords do not match')
      return
    }

    try {
      setLoading(true)
      const { resetPasswordApi } = await import('../apis/auth.api')
      await resetPasswordApi(email, newPassword)
      setSuccess(true)
    } catch (err) {
      setError(err?.error || err?.message || 'Failed to reset password. Please try again.')
    } finally {
      setLoading(false)
    }
  }

  const handleLoginNow = () => {
    navigate('/signin')
  }

  return (
    <div className="min-h-screen flex items-center justify-center bg-gray-50 p-4">
      {/* ===== BACKGROUND CIRCLES ===== */}
      <div className="absolute -top-24 -left-24 w-72 h-72 bg-[#008300]/10 rounded-full"></div>
      <div className="absolute top-40 right-0 w-64 h-64 bg-[#0B1B3F]/10 rounded-full"></div>
      <div className="w-full max-w-md bg-white rounded-2xl shadow-md p-8">
        {!success ? (
          <>
            <h2 className="text-2xl text-center font-bold mb-6">Update Password</h2>

            {/* New Password Input */}
            <div className="mb-4 relative">
              <input
                type={showNewPassword ? 'text' : 'password'}
                value={newPassword}
                onChange={(e) => setNewPassword(e.target.value)}
                placeholder="Enter new password"
                className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-[#008300] pr-10"
              />
              <button
                type="button"
                onClick={() => setShowNewPassword(!showNewPassword)}
                className="absolute right-3 top-3 text-gray-500 hover:text-gray-700"
              >
                {showNewPassword ? <AiOutlineEyeInvisible size={20} /> : <AiOutlineEye size={20} />}
              </button>
            </div>

            {/* Re-Enter Password Input */}
            <div className="mb-4 relative">
              <input
                type={showReEnterPassword ? 'text' : 'password'}
                value={reEnterPassword}
                onChange={(e) => setReEnterPassword(e.target.value)}
                placeholder="Re-enter new password"
                className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-[#008300] pr-10"
              />
              <button
                type="button"
                onClick={() => setShowReEnterPassword(!showReEnterPassword)}
                className="absolute right-3 top-3 text-gray-500 hover:text-gray-700"
              >
                {showReEnterPassword ? <AiOutlineEyeInvisible size={20} /> : <AiOutlineEye size={20} />}
              </button>
            </div>

            {/* Error Message */}
            {error && <p className="text-sm text-red-500 mb-4">{error}</p>}

            {/* Reset Now Button */}
            <button
              onClick={handleResetPassword}
              disabled={loading}
              className={`w-full text-white py-2 rounded-lg font-semibold transition ${loading ? 'bg-[#006600]' : 'bg-[#008300] hover:bg-[#006600]'}`}
            >
              {loading ? 'Resetting...' : 'Reset Now'}
            </button>
          </>
        ) : (
          <>
            <h2 className="text-2xl font-bold mb-4 text-green-600">Password Changed Successfully!</h2>
            <p className="text-gray-600 mb-6">Your password has been updated. Please log in with your new password.</p>

            <button
              onClick={handleLoginNow}
              className="w-full bg-[#008300] text-white py-2 rounded-lg font-semibold hover:bg-blue-700 transition"
            >
              Login Now
            </button>
          </>
        )}
      </div>
    </div>
  )
}
